import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popper,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Editor } from '@tinymce/tinymce-react';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom/model';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/image';
import 'tinymce/plugins/table';
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';
import 'tinymce/skins/content/default/content.min.css';
import { handleField } from 'common/utils/fieldHelper';
import { useTranslation } from 'common/utils/getLocalString';
import { LanguageMap } from 'common/utils/languageMap';

import IssueFileUpload from '../../../../../../src/components/issueCreateEdit/components/IssueFileUpload';
import CalendarFormIcon from '../../icons/CalendarFormIcon';
import CheckboxIcon from '../../icons/CheckboxIcon';
import CheckboxIconChecked from '../../icons/CheckboxIconChecked';
import ChevronIcon from '../../icons/ChevronIcon';
import ClearFormFieldIcon from '../../icons/ClearFormFieldIcon';
import DeleteIcon from '../../icons/DeleteIcon';

// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(() => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    position: 'static',
    color: '#41424E',
    lineHeight: '20px',
    transform: 'none',
    display: 'flex',
    flexShrink: 0,
    '& .MuiFormLabel-asterisk': {
      color: '#E03737',
    },
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
  },
}));
// Original class: textField
const StyledTextField = styled(TextField)(() => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: 'auto',
    '&.MuiInputBase-multiline': {
      padding: 0,
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      fontSize: '12px',
      lineHeight: '20px',
      padding: '8px 16px',
      height: 'auto',
      color: '#212346',
      '&::placeholder': {
        color: '#7174AC',
        fontSize: '12px',
        lineHeight: '20px',
      },
    },
    '&.Mui-disabled': {
      '& .MuiInputBase-input.MuiOutlinedInput-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
    },
  },
}));
// Original class: booleanWrap
const StyledBooleanWrap = styled(RadioGroup)(() => ({
  flexDirection: 'row !important',
}));
// Original class: booleanLabel
const StyledBooleanLabel = styled(FormControlLabel, {
  shouldForwardProp: prop => !['custom', 'checkedStyle'].includes(prop),
})(({ custom, checkedStyle }) => ({
  '&.MuiFormControlLabel-root': {
    margin: 0,
    ...(custom && {
      fontSize: '14px',
      color: '#9EA8E2',
      textTransform: 'uppercase',
      marginRight: '10px',
      '& .MuiRadio-root': {
        display: 'none',
      },
    }),
    ...(checkedStyle && {
      color: '#212346',
      textDecoration: 'underline',
      '& .MuiFormControlLabel-label': {
        fontWeight: '700',
      },
    }),
  },
}));
// Original class: fieldAutocomplete
const StyledFieldAutocomplete = styled(Autocomplete, {
  shouldForwardProp: prop =>
    !['multipleStyle', 'borderlessStyle'].includes(prop),
})(({ multipleStyle, borderlessStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    background: '#fff',
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    ...(multipleStyle && {
      minHeight: '36px',
      padding: '0 65px 0 0',
    }),
    '& .MuiOutlinedInput-notchedOutline': {
      ...(borderlessStyle && {
        borderColor: 'transparent',
      }),
    },
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      padding: '0',
      height: 'auto',
      ...(multipleStyle && {
        marginLeft: '16px',
      }),
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
    '&:hover': {
      '&.MuiInputBase-root': {
        '&.MuiOutlinedInput-root': {
          backgroundColor: '#fff',
        },
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          padding: '8px 10px',
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '& .MuiButtonBase-root.MuiCheckbox-root': {
            padding: '2px',
            marginRight: '10px',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));

// Original class: autocompletePopper
const StyledAutocompletePopper = styled(Popper)(() => ({
  zIndex: 9000,

  '& .MuiPaper-root.MuiAutocomplete-paper': {
    borderRadius: '0 0 16px 16px',
    border: '1px solid #E4E4EF',
    borderTop: 'none',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '& .MuiAutocomplete-noOptions': {
      padding: '8px 16px',
      color: '#212346',
      fontSize: '12px',
      lineHeight: '20px',
    },
    '& .MuiAutocomplete-listbox': {
      padding: '0',
      '& li': {
        padding: '8px 10px',
        whiteSpace: 'normal',
        fontSize: '12px',
        lineHeight: '20px',
        color: '#212346',
        '& .MuiButtonBase-root.MuiCheckbox-root': {
          padding: '2px',
          marginRight: '10px',
        },
        '&.Mui-focused': {
          backgroundColor: 'transparent',
        },
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-selected': {
          backgroundColor: '#DCDFF4',
        },
      },
      '&::-webkit-scrollbar': {
        width: '2px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
  },
}));
// Original class: chip
const StyledChip = styled(Chip)(() => ({
  '&.MuiChip-root': {
    height: '30px',
    margin: '3px 4px',
    fontSize: '12px',
    display: 'flex',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      position: 'static',
      height: '10px !important',
    },
  },
}));
// Original class: datePicker
const StyledDatePicker = styled(DatePicker)(() => ({
  '&.MuiFormControl-root.MuiTextField-root': {
    height: 'auto',
    width: '116px',
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      height: 'auto',
      padding: 0,
      '&.Mui-disabled': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#E4E4EF',
        },
        '& .MuiInputAdornment-root': {
          '& .MuiSvgIcon-root path': {
            fill: '#E4E4EF',
          },
        },
      },
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      height: 'auto',
      padding: '8px 30px 8px 16px',
      textTransform: 'lowercase',
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input::placeholder': {
      textTransform: 'lowercase',
    },
  },

  '& .MuiInputAdornment-root': {
    position: 'absolute',
    right: '20px',
  },
}));
// Original class: tooltipForm
const StyledTooltipForm = styled(Popper)(() => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#F1F2F8',
    borderRadius: '8px',
    padding: '4px',
    color: '#212346',
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: 400,
    margin: 0,
    marginBottom: '0!important',
  },
}));

const FormField = ({
  field,
  fieldEvent,
  bulk,
  changeSearchInput,
  searchFieldOptions,
  searchLoading,
  smallLabel,
  borderless,
  fullWidth,
}) => {
  const getLocalString = useTranslation();
  const textField = useRef(['link', 'string']);
  const numberFields = useRef(['float', 'progressbar', 'int']);
  const autocompleteFields = useRef([
    'list',
    'enumeration',
    'user',
    'project',
    'tracker',
    'trackers',
  ]);
  const searchFields = useRef(['timesheet_issue', 'issue_parent']);
  const [singleField, setSingleField] = useState(false);
  const [fieldError, setFieldError] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const validateUrl = useCallback((value, field) => {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' + // validate protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
        '(\\#[-a-z\\d_]*)?$',
      'i',
    );
    value !== '' &&
      field === 'link' &&
      !urlPattern.test(value) &&
      setFieldError(true);
    value !== '' &&
      field === 'link' &&
      urlPattern.test(value) &&
      setFieldError(false);
  }, []);

  const [language, setLanguage] = useState('ru');

  useEffect(() => {
    window.localStorage.getItem('lang') &&
      setLanguage(LanguageMap.get(window.localStorage.getItem('lang')).lang);
  }, []);

  useEffect(() => {
    const labelTopFields = ['description', 'notes', 'trackers'];
    if (labelTopFields.includes(field.fieldId)) {
      setSingleField(true);
    }
  }, [field.fieldId]);

  const handleFormField = useCallback(
    (data, field, deleted = false) => {
      const fieldValue = handleField(data, field, deleted);
      fieldEvent(fieldValue, field);
    },
    [fieldEvent],
  );
  const handleEditorChange = useCallback(
    (data, field) => {
      handleFormField(data, field);
    },
    [handleFormField],
  );

  const handleNumberField = useCallback(
    (value, field) => {
      let reg = new RegExp(/^[0-9,.-]*$/);
      field.fieldDefinition.fieldFormat === 'progressbar' &&
        (reg = new RegExp(/^[0-9]*$/));
      if (reg.test(value)) {
        handleFormField(value, field);
      }
    },
    [handleFormField],
  );

  const addNewValue = useCallback(
    newValue => {
      let currentField = field;
      const isExisting = field.fieldDefinition.values.some(
        option =>
          option.valueName.toLowerCase() === newValue.trim().toLowerCase(),
      );
      if (!isExisting) {
        currentField.fieldDefinition.values.unshift({
          valueId: `new${currentField.fieldDefinition.values.length + 1}`,
          valueName: newValue,
        });
      }
      if (
        currentField.valueId.find(
          item => item.valueName === newValue.trim(),
        ) === undefined
      ) {
        const value = [
          {
            valueId: `new${currentField.fieldDefinition.values.length}`,
            valueName: newValue,
          },
          ...currentField.valueId,
        ];
        handleFormField(value, field);
      }
      setInputValue('');
    },
    [field, handleFormField],
  );
  const handleKeyDown = useCallback(
    (event, params) => {
      if (event.key === 'Enter' && params.inputProps.value.trim() !== '') {
        event.preventDefault();
        addNewValue(params.inputProps.value.trim());
        setInputValue('');
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    [addNewValue],
  );

  const handleInputChange = useCallback((event, newInputValue) => {
    setInputValue(newInputValue);
  }, []);

  return (
    <Grid
      container
      sx={{
        alignItems: 'center',
        flexWrap: 'nowrap',
        position: 'relative',
        marginTop: '16px',
      }}
    >
      <FormControl
        required={field.fieldDefinition.isRequired}
        key={field.fieldId}
        sx={{
          display: 'flex',
          flexDirection:
            smallLabel && field.fieldDefinition.fieldId === 'description'
              ? 'column'
              : 'row',
          flex: 1,
          alignItems: singleField ? 'flex-start' : 'center',
          justifyContent: fullWidth ? 'space-between' : 'flex-start',
        }}
      >
        <StyledSelectLabel
          sx={{
            marginTop: singleField ? '8px' : 0,
            width: smallLabel ? '100px' : '200px',
            paddingRight: '10px',
          }}
        >
          <Typography
            variant='h5'
            sx={{
              color: '#41424E',
            }}
          >
            {`${field.fieldName}:`}
          </Typography>
        </StyledSelectLabel>
        {numberFields.current.includes(field.fieldDefinition.fieldFormat) && (
          <StyledTextField
            disabled={field.deleted || field.fieldDefinition.isReadonly}
            error={field.error}
            required={field.fieldDefinition.isRequired}
            variant='outlined'
            sx={{ width: 250 }}
            placeholder={getLocalString('labels', 'input')}
            value={field.valueId ? field.valueId : ''}
            onChange={event => handleNumberField(event.target.value, field)}
            slotProps={{
              htmlInput: {
                maxLength: field.fieldDefinition.maxLength,
                minLength: field.fieldDefinition.minLength,
              },
            }}
          ></StyledTextField>
        )}
        {(field.fieldDefinition.fieldId === 'description' ||
          field.fieldDefinition.fieldFormat === 'text') && (
          <Grid
            container
            sx={{
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <Editor
              init={{
                setup: editor => {
                  editor.on('change input', () => {
                    handleEditorChange(editor.getContent(), field);
                  });
                },
                skin: false,
                content_css: false,
                height: 200,
                menubar: false,
                plugins:
                  field.fieldDefinition.fieldId === 'description'
                    ? 'image autolink'
                    : '',
                toolbar: 'formatselect | bold italic backcolor',
                link_default_target: '_blank',
                branding: false,
                content_style: `
          img {
            max-width: 100%;
            height: auto;
          }
        `,
              }}
              value={field.valueName ? field.valueName : ''}
              //onEditorChange={content => handleEditorChange(content, field)}
              disabled={field.deleted || field.fieldDefinition.isReadonly}
            />
          </Grid>
        )}
        {field.fieldDefinition.fieldId !== 'description' &&
          textField.current.includes(field.fieldDefinition.fieldFormat) && (
            <StyledTextField
              onBlur={event =>
                validateUrl(
                  event.target.value,
                  field.fieldDefinition.fieldFormat,
                )
              }
              disabled={field.deleted || field.fieldDefinition.isReadonly}
              error={field.error}
              multiline={singleField}
              rows={singleField ? 3 : ''}
              required={field.fieldDefinition.isRequired}
              sx={{ width: 353 }}
              variant='outlined'
              placeholder={getLocalString('labels', 'input')}
              value={field.valueId ? field.valueId : ''}
              onChange={event => handleFormField(event.target.value, field)}
            ></StyledTextField>
          )}
        {field.fieldDefinition.fieldFormat === 'bool' && (
          <StyledBooleanWrap
            disabled={field.deleted || field.fieldDefinition.isReadonly}
            required={field.fieldDefinition.isRequired}
            onChange={(event, value) => handleFormField(value, field)}
            value={field.valueId}
          >
            <StyledBooleanLabel
              value={true}
              checked={
                (field.valueId !== '' && field.valueId === 'true') ||
                field.valueId === true
              }
              control={
                <Radio
                  disableRipple
                  icon={
                    field.error ? (
                      <CheckboxIcon
                        color={'#E03737'}
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    ) : (
                      <CheckboxIcon
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    )
                  }
                  checkedIcon={
                    <CheckboxIconChecked
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                    />
                  }
                />
              }
              label={
                <Typography variant='h5'>
                  {getLocalString('labels', 'yes')}
                </Typography>
              }
            />
            <StyledBooleanLabel
              value={false}
              checked={
                (field.valueId !== '' && field.valueId === 'false') ||
                field.valueId === false
              }
              control={
                <Radio
                  disableRipple
                  icon={
                    field.error ? (
                      <CheckboxIcon
                        color={'#E03737'}
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    ) : (
                      <CheckboxIcon
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    )
                  }
                  checkedIcon={
                    <CheckboxIconChecked
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                    />
                  }
                />
              }
              label={
                <Typography variant='h5'>
                  {getLocalString('labels', 'no')}
                </Typography>
              }
            />
          </StyledBooleanWrap>
        )}
        {field.fieldDefinition.fieldFormat === 'language' && (
          <StyledBooleanWrap
            disabled={field.deleted || field.fieldDefinition.isReadonly}
            required={field.fieldDefinition.isRequired}
            onChange={(event, value) => handleFormField(value, field)}
            value={field.valueId}
          >
            {field.fieldDefinition.values.map((item, index) => (
              <StyledBooleanLabel
                custom={true}
                checkedStyle={field.valueId === item.valueId}
                key={index}
                control={<Radio />}
                value={item.valueId}
                checked={field.valueId !== '' && field.valueId === item.valueId}
                label={item.valueName}
              />
            ))}
          </StyledBooleanWrap>
        )}
        {searchFields.current.includes(field.fieldDefinition.fieldFormat) && (
          <StyledFieldAutocomplete
            disabled={field.deleted || field.fieldDefinition.isReadonly}
            noOptionsText={getLocalString('labels', 'notFound')}
            filterOptions={x => x}
            required={field.fieldDefinition.isRequired}
            onChange={(event, newValue) => handleFormField(newValue, field)}
            disablePortal
            options={searchFieldOptions}
            sx={{ width: 253 }}
            disableClearable={bulk}
            renderOption={(props, option) => {
              return (
                <MenuItem
                  {...props}
                  selected={option.valueId === field.valueId}
                  key={option.valueId}
                  value={option.valueId + option.valueName}
                >
                  {option.valueName}
                </MenuItem>
              );
            }}
            value={field.valueId}
            getOptionLabel={option => option.valueName || ''}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            loading={searchLoading}
            renderInput={params => (
              <TextField
                {...params}
                error={field.error}
                onChange={ev => changeSearchInput(ev.target.value)}
                placeholder={getLocalString('labels', 'input')}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {searchLoading ? (
                          <CircularProgress color='inherit' size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  },
                }}
              />
            )}
            popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
            slots={{
              popper: props => (
                <Popper {...props} placement='bottom-start'></Popper>
              ),
            }}
          />
        )}
        {autocompleteFields.current.includes(
          field.fieldDefinition.fieldFormat,
        ) &&
          !field.fieldDefinition.isMultiple && (
            <StyledFieldAutocomplete
              borderlessStyle={borderless}
              noOptionsText={getLocalString('labels', 'notFound')}
              disabled={field.deleted || field.fieldDefinition.isReadonly}
              required={field.fieldDefinition.isRequired}
              onChange={(event, newValue) => handleFormField(newValue, field)}
              //disablePortal
              options={
                field.fieldDefinition.values ? field.fieldDefinition.values : []
              }
              sx={{ width: fullWidth ? 353 : 253 }}
              disableClearable={bulk}
              renderOption={(props, option) => {
                return (
                  <MenuItem
                    {...props}
                    key={option.valueId + option.valueName}
                    value={option.valueId}
                    selected={
                      field.valueId &&
                      option.valueId.toString() ===
                        field.valueId.valueId.toString()
                    }
                  >
                    {option.valueName}
                  </MenuItem>
                );
              }}
              value={field.valueId}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              getOptionLabel={option => option.valueName || ''}
              renderInput={params => {
                return (
                  <TextField
                    {...params}
                    error={field.error}
                    placeholder={getLocalString('labels', 'select')}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: field.color || '#212346',
                      },
                    }}
                  />
                );
              }}
              popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
              slots={{
                popper: props => (
                  <StyledAutocompletePopper
                    {...props}
                    placement='bottom-start'
                  ></StyledAutocompletePopper>
                ),
              }}
            />
          )}
        {autocompleteFields.current.includes(
          field.fieldDefinition.fieldFormat,
        ) &&
          field.fieldDefinition.isMultiple && (
            <StyledFieldAutocomplete
              multipleStyle={true}
              forcePopupIcon={false}
              multiple
              disabled={field.deleted || field.fieldDefinition.isReadonly}
              disableClearable
              required={field.fieldDefinition.isRequired}
              //open={true}
              onChange={(event, newValue) => handleFormField(newValue, field)}
              options={field.fieldDefinition.values}
              sx={{ width: '100%' }}
              disableCloseOnSelect
              disablePortal
              value={field.valueId ? field.valueId : []}
              isOptionEqualToValue={(option, value) =>
                option.valueId === value.valueId
              }
              getOptionLabel={option => option.valueName || ''}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.valueId}>
                  <Checkbox
                    icon=<CheckboxIcon
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                    />
                    checkedIcon=<CheckboxIconChecked
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                    />
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.valueName}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <StyledChip
                    {...getTagProps({ index })}
                    key={index}
                    deleteIcon={
                      <DeleteIcon width={10} height={10} viewBox='0 0 10 10' />
                    }
                    variant='outlined'
                    label={option.valueName}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  error={field.error}
                  placeholder={getLocalString('labels', 'input')}
                />
              )}
            />
          )}
        {field.fieldDefinition.fieldFormat === 'tags' && (
          <StyledFieldAutocomplete
            multipleStyle={true}
            freeSolo
            forcePopupIcon={false}
            multiple
            disabled={field.deleted || field.fieldDefinition.isReadonly}
            disableClearable
            //open={true}
            onChange={(event, newValue, reason) => {
              if (reason === 'selectOption' || reason === 'removeOption') {
                handleFormField(newValue, field);
              }
            }}
            options={field.fieldDefinition.values}
            sx={{ width: '100%' }}
            disableCloseOnSelect
            disablePortal
            value={field.valueId ? field.valueId : []}
            isOptionEqualToValue={(option, value) =>
              option.valueId === value.valueId
            }
            filterOptions={(options, params) => {
              const filtered = field.fieldDefinition.values.filter(option =>
                option.valueName
                  .toLowerCase()
                  .includes(params.inputValue.toLowerCase()),
              );
              if (
                params.inputValue !== '' &&
                !field.fieldDefinition.values.some(
                  option =>
                    option.valueName.toLowerCase() ===
                    params.inputValue.toLowerCase(),
                )
              ) {
                return [
                  ...filtered,
                  {
                    isAddOption: true,
                    valueName: params.inputValue,
                    valueId: `new${field.fieldDefinition.values.length + 1}`,
                  },
                ];
              }

              return filtered;
            }}
            getOptionLabel={option => {
              if (option.isAddOption) {
                return option.valueName;
              }
              return option.valueName || '';
            }}
            renderOption={(props, option, { selected }) => {
              if (option.isAddOption) {
                return (
                  <li
                    {...props}
                    key={option.valueId}
                    onClick={() => addNewValue(option.valueName)}
                  >
                    {`${getLocalString('buttons', 'add')} ${option.valueName}`}
                  </li>
                );
              } else {
                return (
                  <li {...props} key={option.valueId}>
                    <Checkbox
                      icon=<CheckboxIcon
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                      checkedIcon=<CheckboxIconChecked
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.valueName}
                  </li>
                );
              }
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <StyledChip
                  {...getTagProps({ index })}
                  key={index}
                  deleteIcon={
                    <DeleteIcon width={10} height={10} viewBox='0 0 10 10' />
                  }
                  variant='outlined'
                  label={option.valueName}
                />
              ))
            }
            inputValue={inputValue}
            onInputChange={handleInputChange}
            renderInput={params => {
              return (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  onKeyDown={event => handleKeyDown(event, params, field)}
                  placeholder={getLocalString('labels', 'input')}
                />
              );
            }}
          />
        )}
        {field.fieldDefinition.fieldFormat === 'date' && (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={language}
          >
            <StyledDatePicker
              required={field.fieldDefinition.isRequired}
              disabled={field.deleted || field.fieldDefinition.isReadonly}
              format={'DD.MM.YYYY'}
              slots={{
                openPickerIcon: ({ ...props }) => (
                  <CalendarFormIcon {...props} />
                ),
                leftArrowIcon: () => (
                  <ChevronIcon
                    direction='left'
                    viewBox='0 0 24 24'
                    width={24}
                    height={24}
                  />
                ),
                rightArrowIcon: () => (
                  <ChevronIcon
                    direction='right'
                    viewBox='0 0 24 24'
                    width={24}
                    height={24}
                  />
                ),
                rightArrowButton: componentProps => (
                  <IconButton disableRipple {...componentProps}>
                    {componentProps.children}
                  </IconButton>
                ),
                leftArrowButton: componentProps => (
                  <IconButton disableRipple {...componentProps}>
                    {componentProps.children}
                  </IconButton>
                ),
              }}
              slotProps={{
                textField: {
                  inputProps: { placeholder: 'dd.mm.yyyy' },
                },
                field: {
                  placeholder: 'dd.mm.yyyy',
                },
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={field.error}
                  placeholder={getLocalString('labels', 'input')}
                />
              )}
              value={
                field.valueId && field.valueId !== ''
                  ? dayjs(field.valueId)
                  : null
              }
              onChange={value => handleFormField(value, field)}
            />
          </LocalizationProvider>
        )}
        {field.fieldDefinition.fieldFormat === 'attachment' && (
          <IssueFileUpload
            handleUpload={value => handleFormField(value, field)}
            data={{ valueId: field.valueId, valueName: field.valueName }}
          ></IssueFileUpload>
        )}
      </FormControl>
      {bulk && !field.fieldDefinition.isRequired && (
        <Tooltip
          placement='top-start'
          title={getLocalString('labels', 'clear')}
          slots={{
            popper: StyledTooltipForm,
          }}
        >
          <IconButton
            onClick={value => handleFormField(value, field, true)}
            disableRipple
            sx={{ padding: 0, marginLeft: '2px' }}
          >
            <ClearFormFieldIcon color={field.deleted ? '#E4E4EF' : '#7174AC'} />
          </IconButton>
        </Tooltip>
      )}
      {fieldError && (
        <Box sx={{ position: 'absolute', bottom: '-17px', left: '205px' }}>
          <Typography
            variant='h5'
            sx={{
              color: '#E03737',
            }}
          >
            {getLocalString('errors', 'urlError')}
          </Typography>
        </Box>
      )}
    </Grid>
  );
};

export default FormField;
